import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { BASE_TOKEN_CONFIG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

// Global currency lens (ported from the DPIN build): which base token the whole
// app operates in — market feed, ticker and balance/portfolio widgets all follow
// it. Always a concrete token (no "All"): a mixed feed shows two collaterals with
// the same ¢ notation, which misprices one of them. Per-market pages still render
// in the market's own collateral regardless of the lens (money correctness).
// Dropdown rather than a segmented toggle — it's a low-frequency mode switch,
// header space is scarce, and the list scales past two tokens.
export const SwitchBaseToken = () => {
  const { t } = useTranslation();
  const baseToken = useAppSettingStore(state => state.baseToken);
  const setBaseToken = useAppSettingStore(state => state.setBaseToken);
  // Hide on market/event detail + invite pages: those price in the market's own
  // collateral, so a global lens toggle there is noise. (useRoute doesn't take a
  // RegExp in this wouter version — test the location string directly.)
  const [location] = useLocation();
  // invite-earn now shows the lens: its referral earnings are per-currency and
  // follow the selected token like the rest of the app (2026-07-15). market/event
  // stay hidden — those render in the market's OWN collateral, not the global lens.
  if (/^\/(?:market|event)\//.test(location)) return null;
  if (BASE_TOKEN_CONFIG.length < 2) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          title={t(baseToken.tooltipKey)}
          className="px-2 h-9 rounded-md gap-1.5 shadow-none border border-foreground/4 bg-foreground/4 hover:bg-foreground/8"
        >
          <baseToken.icon className="size-4.5" />
          <span className="text-sm font-medium leading-5 text-current">{t(baseToken.translateKey)}</span>
          <ChevronDown className="size-3.5 op-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {BASE_TOKEN_CONFIG.map(item => (
          <DropdownMenuItem
            key={item.address}
            className={cn('gap-2 cursor-pointer', baseToken.address === item.address && 'bg-foreground/5')}
            onClick={() => setBaseToken(item)}
          >
            <item.icon className="size-4.5" />
            <span className="flex-1 text-sm font-medium">{t(item.translateKey)}</span>
            <span className="text-xs text-muted-foreground">{item.name}</span>
            {baseToken.address === item.address && <Check className="size-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
